import { t } from 'i18next'
import { z } from 'zod'
import { DataTypes } from '../../../enums'
import { IFormType } from '../../../generated-types/form-type'

export const certificateSchema = z.object({
  certificateNumber: z
    .string()
    .nonempty()
    .regex(/^(?! *$).+/, t('certificateNumberIsInvalid', { ns: 'certificate' })),
  certificateDate: z.date().or(z.string()),
  validUntil: z.date().or(z.string()).optional(),
  certificateDocumentType: z.number(),
  certificateFileId: z.union([z.string(), z.number()]),
  certificateSupplierId: z
    .object({
      id: z.number(),
    })
    .transform(value => value.id),
  validPeriod: z
    .object({
      id: z.number(),
    })
    .transform(value => value.id),
})

export const getMainComponentSchema = (formType: IFormType, translate: any, isCertificateDetailsRequired?: boolean) => {
  const createSchema = z
    .object({
      typeId: z.number(),
      description: z.string().nullable().optional(),
      serialNumber: z
        .string()
        .nonempty(translate('serialNumberIsRequired'))
        .refine(sn => sn.trim() !== '', {
          message: translate('serialNumberIsRequired'),
        })
        .transform(sn => sn.trim()),
      name: z.string().nullable().optional(),
      orderNumber: z.string().nullable().optional(),
      dateProduced: z.date().or(z.string()).nullable().optional(),
      lifetime: z
        .string()
        .or(z.number())
        .transform(v => Number(v))
        .nullable()
        .optional(),
      supplierId: z
        .object({
          id: z.number(),
        })
        .transform(value => value.id),
      reference: z.string().nullable().optional(),
      productionMonth: z.any().nullable().optional(),
      productionYear: z.string().nullable().optional(),
      fileId: z.union([z.string(), z.number()]).nullable().optional(),
      positionId: z.number().nullable().optional(),
      status: z.number().nullable().optional(),
      circumference: z
        .number()
        .or(z.string())
        .nullable()
        .transform(x => (typeof x === 'string' ? Number(x) : x))
        .refine(value => value == null || value >= 0, { message: translate('mustBePositive', { ns: 'common' }) })
        .optional(),
      solidity: z
        .number()
        .or(z.string())
        .nullable()
        .transform(x => (typeof x === 'string' ? Number(x) : x))
        .refine(value => value == null || value >= 0, { message: translate('mustBePositive', { ns: 'common' }) })
        .optional(),
      mainComponentProperties: z
        .array(
          z.object({
            mainComponentId: z.number().or(z.null()).optional(),
            hs: z.number().or(z.string()),
            vC5: z.number().or(z.string()),
            vC15: z.number().or(z.string()).nullable().optional(),
          }),
        )
        .nullable()
        .optional(),
      mainComponentMaintenanceTemplates: z
        .array(
          z.object({
            id: z.number(),
            name: z.string(),
          }),
        )
        .nullable()
        .optional(),
    })
    .merge(isCertificateDetailsRequired ? certificateSchema : z.object({}))

  const updateSchema = z.object({
    id: z.number(),
    name: z.string().nullable().optional(),
    typeId: z.number(),
    description: z.string().nullable().optional(),
    serialNumber: z
      .string()
      .nonempty(translate('serialNumberIsRequired'))
      .refine(sn => sn.trim() !== '', {
        message: translate('serialNumberIsRequired'),
      })
      .transform(sn => sn.trim()),
    orderNumber: z.string().nullable().optional(),
    dateProduced: z.any().nullable().optional(),
    lifetime: z
      .string()
      .or(z.number())
      .transform(v => Number(v))
      .nullable()
      .optional(),
    supplierId: z
      .object(
        {
          id: z.number(),
        },
        { invalid_type_error: translate('required', { ns: 'common' }) },
      )
      .transform(value => value.id),
    reference: z.string().nullable().optional(),
    productionMonth: z.any().or(z.null()).optional(),
    productionYear: z.string().or(z.null()).optional(),
    customPropertyValues: z.string().optional(),
    fileId: z.union([z.any(), z.any()]).optional(),
    positionId: z.any().optional(),
    status: z.number().optional(),
    circumference: z
      .number()
      .or(z.string())
      .or(z.null())
      .transform(value => (typeof value === 'string' ? (value == '' ? null : Number(value)) : value))
      .refine(value => value == null || value >= 0, { message: translate('mustBePositive', { ns: 'common' }) })
      .optional(),
    solidity: z
      .number()
      .or(z.string())
      .or(z.null())
      .transform(value => (typeof value === 'string' ? (value == '' ? null : Number(value)) : value))
      .refine(value => value == null || value >= 0, { message: translate('mustBePositive', { ns: 'common' }) })
      .optional(),
    mainComponentProperties: z
      .array(
        z.object({
          mainComponentId: z.number().or(z.null()).optional(),
          hs: z.number().or(z.string()),
          vC5: z.number().or(z.string()),
          vC15: z.number().or(z.string()).optional().nullable(),
        }),
      )
      .or(z.null())
      .optional(),
    mainComponentMaintenanceTemplates: z
      .array(
        z.object({
          id: z.number(),
          name: z.string(),
        }),
      )
      .optional()
      .nullable(),
  })

  switch (formType) {
    case IFormType.Create:
      return createSchema
    case IFormType.Update:
      return updateSchema
    default:
      return createSchema
  }
}

export function addCustomFields(schema, customFields, translate) {
  const customFieldSchemas = {}
  customFields.forEach(field => {
    customFieldSchemas[field.id.toString()] = getFieldSchema(field)
    customFieldSchemas[JSON.parse(field.label as string).en] = getFieldSchema(field)
  })
  let baseSchema
  if (schema instanceof z.ZodObject) {
    baseSchema = schema
  } else if (schema instanceof z.ZodEffects && schema._def.schema instanceof z.ZodObject) {
    baseSchema = schema._def.schema
  } else {
    throw new Error(translate('unExpectedError', { ns: 'common-api-message' }))
  }

  return baseSchema.extend(customFieldSchemas)
}

function getFieldSchema(field) {
  switch (field.dataType) {
    case DataTypes.Integer:
      return z
        .string()
        .or(z.number())
        .or(z.null())
        .transform(value => (value === '' || value === null || value === undefined ? null : Number(value)))
        .nullable()
        .optional()
    case DataTypes.String:
      return z.string().or(z.null()).optional()
    default:
      return z.any() // Default to any type if data type is not recognized
  }
}
